import { Box, Divider, Flex, HStack, Tooltip, useToast } from '@chakra-ui/react'
import kalvium from '@kalvi-education/kalvium-sdk'
// import KC from '@kalvi-education/kalvium-sdk/src/keycloak2.js'
import { useInView } from 'framer-motion'
import { useEffect, useRef } from 'react'
import { useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
    assessmentDivider,
    defaultBorder,
    thickBoxShadow,
} from '../../../chakraTheme'
import Content from '../../../common/AssessmentInstructionsContent'
import { AuthContext } from '../../../contexts/AuthContext.js'
import AxInstructionsContext from '../../../contexts/AxInstructionsContext'
import ASSESSMENT_TYPE from '../../../enums/assessmentTypes.js'
import AX_INSTRUCTIONS_ACTION from '../../../enums/axInstructionsActions'
import AX_INSTRUCTIONS_STATUS from '../../../enums/axInstructionsStatus'
import { saveEventLog } from '../../../pages/Attempt/_service.js'
import {
    cacheRegularAssessmentAttemptStorage,
    getRegularAssessmentAttemptStorage,
    REGULAR_ASSESSMENT_SOURCE_FLOW,
} from '../../../utils/regularAssessmentStorage'
import { ArrowForward, Info } from '../../Icons/default'
import PrimaryButton from '../../PrimaryButton.jsx'
import ASSESSMENT_STATUS from '@/enums/assessmentStatus.js'
import useProctoring from '@/hooks/useProctoring/useProctoring.js'
import FULL_SCREEN_EXIT_STATES from '@/enums/fullScreenExitStates.js'
import proctoringGlitchRefresh from '@/utils/proctoringGlitchRefresh'

const NavFooter = ({ nextBtnRef }) => {
    const { store, dispatch } = useContext(AxInstructionsContext)
    const { kalviumKc } = useContext(AuthContext)
    const { getBrowserName, getBrowserVersion, getOS, getOSVersion } = useProctoring()
    const pathParams = useParams()
    const toast = useToast()
    const navigate = useNavigate()
    const hasUnclosedAttempt = !!store?.attempts?.some(attempt => !attempt?.closed)
    const isExistingAttempts = hasUnclosedAttempt && !store.retryEnabled
    const isPreview = store?.isPreview === true
    const isNextDisabled = false
    const isOpenAssessment = store?.assessment.visibility === 'open'
    const isAssessmentActive = store?.assessment?.status === ASSESSMENT_STATUS.PUBLISHED

    const browserDetails = `Browser: ${getBrowserName()} ${getBrowserVersion()}`
    const osDetails = `OS: ${getOS()} ${getOSVersion()}`
    const systemInfo = `${browserDetails}, ${osDetails}`

    // Prevents duplicate resume/started logs when the button is clicked multiple times
    // or when both user click and auto-resume effect trigger handleNext concurrently.
    const isHandlingNextRef = useRef(false)

    const handleNext = async (isAutoSubmitOnGoingAttempt = false) => {
        if (isHandlingNextRef.current) return
        isHandlingNextRef.current = true
        // create an attempt

        const isScreenExtend =  window.screen.isExtended 
        // if(isScreenExtend && !isAutoSubmitOnGoingAttempt){
        //     toast({
        //         title: "External Screen Detected. Please disconnect",
        //         status: 'error',
        //         duration: 3000,
        //         isClosable: true,
        //         position: 'top-right',
        //     })
        //     return;
        // }
        try {
            // Glitch refresh = connection blip, not a violation: skip the life
            // deduction on resume (only when returning within the 30s window).
            const isGlitchResume =
                isExistingAttempts &&
                !isAutoSubmitOnGoingAttempt &&
                proctoringGlitchRefresh.peek(pathParams.ax_slug)
            if (isGlitchResume) proctoringGlitchRefresh.clear()
            const extras =
                !isExistingAttempts && store?.assessment?.allow_extra
                    ? store.formValues
                    : isGlitchResume
                    ? { resume_reason: 'proctoring_glitch' }
                    : undefined

            const data = {
                slug: pathParams.ax_slug,
                otp:
                    isExistingAttempts || !store?.assessment?.is_key_required
                        ? undefined
                        : null,
                // resume_reason/is_preview ride in extras (SDK forwards extras as-is).
                extras: isPreview ? { ...extras, is_preview: true } : extras,
            }
            let attemptData
            if (isOpenAssessment && isExistingAttempts) {
                const { attemptId, token } = getRegularAssessmentAttemptStorage(
                    pathParams.ax_slug,
                    store?.attempts?.[0]
                )
                attemptData = await saveEventLog({
                    attemptId,
                    isTryout: true,
                    token,
                    events: [],
                })
            } else {
                attemptData = isOpenAssessment
                    ? await kalvium.services.assessment.createOpenAxAttempt({
                          ...data,
                          user_id: kalviumKc.getParsedToken().sub,
                      })
                    : await kalvium.services.assessment.createAxAttempt(data)
                // Best-effort cache; a full localStorage must not block the start
                // (the attempt id/token are also carried in router state). KAL-3676.
                cacheRegularAssessmentAttemptStorage(
                    pathParams.ax_slug,
                    attemptData
                )
            }


            if(attemptData?.id && !isAutoSubmitOnGoingAttempt){
                try {
                    await saveEventLog({
                        attemptId: attemptData.id,
                        isTryout: isOpenAssessment,
                        ...(isOpenAssessment && { token: attemptData.token }),
                        events: [{
                            event: isExistingAttempts ? 'resume' : 'started',
                            extras: {
                                content: systemInfo,
                                user_ts: Math.round(new Date().getTime() / 1000),
                            },
                        }],
                    })
                } catch (error) {
                    // Suppress the error silently
                    console.debug('Failed to save event log:', error)
                }
            }

            if (!isAutoSubmitOnGoingAttempt) {
                localStorage.removeItem("exited")
            }

            const stateData = isOpenAssessment
                ? {
                      assessmentType: ASSESSMENT_TYPE.TRYOUT,
                      token: attemptData.token,
                      attemptData: attemptData,
                  }
                : { attemptData }
            if (attemptData.id) {
                localStorage.setItem('prevPage', `/assessment/${pathParams.ax_slug}`)
                // go to attempt page
                navigate(
                    `/assessment/${pathParams.ax_slug}/attempt/${attemptData.id}`,
                    { 
                        replace: isAutoSubmitOnGoingAttempt,
                        state: {
                            ...stateData,
                            isAutoSubmitOnGoingAttempt,
                            sourceFlow: REGULAR_ASSESSMENT_SOURCE_FLOW,
                        }
                    }
                )
            }
        } catch (error) {
            if (error.message === 'OTP verification failed') {
                // show this error in the pin input
                // setFieldError(
                //     'otp',
                //     'The code you’ve entered is incorrect. Please try again.'
                // )
                return
            }
            toast({
                title: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
        } finally {
            isHandlingNextRef.current = false
        }
    }

        useEffect(() => {
        if(store?.fullScreenExitState === FULL_SCREEN_EXIT_STATES.AUTO_SUBMIT_ATTEMPT){
            handleNext(true);
        }
    },[store?.fullScreenExitState])

    return (
        <Flex
            w={'full'}
            py={'6'}
            justifyContent={'space-between'}
            flexDirection={'row-reverse'}
        >
            <Tooltip
                isDisabled={!isNextDisabled}
                hasArrow
                label={
                    <Box fontWeight={'normal'}>
                        <HStack>
                            <Box mr={5}>
                                <Info h={6} w={6} />
                            </Box>
                            <Box marginInlineStart={'0 !important'}>
                                Please enable the required permissions to
                                proceed.
                            </Box>
                        </HStack>
                    </Box>
                }
                placement="top-end"
                bg={'ziggurat'}
                color={'codgray'}
                padding={'6'}
                boxShadow={thickBoxShadow}
                border={defaultBorder}
                borderRadius={'none'}
                offset={[-4, 10]}
                sx={{
                    '.chakra-tooltip__arrow': {
                        boxShadow: '8px 2px 0 #333333 !important',
                    },
                }}
            >
                <Flex>
                    <PrimaryButton
                        wrapperProps={{
                            marginLeft: 'auto',
                            _hover: {},
                            onClick: e => {
                                if(!isAssessmentActive) return e.preventDefault()
                                if (isNextDisabled) return e.preventDefault()
                                if (isExistingAttempts) {
                                    handleNext()
                                    return
                                }

                                dispatch({
                                    type: AX_INSTRUCTIONS_ACTION.UPDATE_CURRENT_STEP_STATUS,
                                    status: AX_INSTRUCTIONS_STATUS.COMPLETED,
                                })
                                dispatch({
                                    type: AX_INSTRUCTIONS_ACTION.NEXT_STEP,
                                })
                            },
                        }}
                        variant={isNextDisabled || !isAssessmentActive ? 'disabled' : 'clickable'}
                        rightIcon={
                            <ArrowForward
                                fontSize={'2xl'}
                                color={isNextDisabled ? 'alto2' : 'white'}
                            />
                        }
                        ref={nextBtnRef}
                        colorScheme={'rum'}
                        my={{ base: 4, md: 0 }}
                        w={{ base: 'full', md: 'auto' }}
                        _hover={{}}
                    >
                        {store?.steps[0]?.status ===
                            AX_INSTRUCTIONS_STATUS.COMPLETED ||
                        !isExistingAttempts
                            ? 'Next'
                            : isExistingAttempts
                            ? 'Resume Assessment'
                            : 'Begin Assessment'}
                    </PrimaryButton>
                </Flex>
            </Tooltip>
        </Flex>
    )
}

const InstructionsWithOtp = () => {
    const { store, dispatch } = useContext(AxInstructionsContext)

    const nextBtnRef = useRef(null)
    // const isExistingAttempts = store?.attempts?.length && !store.retryEnabled

    const isNextBtnInView = useInView(nextBtnRef)

    const markStepAsComplete = () => {
        // prevent unwanted dispatch
        if (
            store?.currentStep &&
            store?.steps &&
            store?.steps[store.currentStep].status ===
                AX_INSTRUCTIONS_STATUS.COMPLETED
        )
            return
        dispatch({
            type: AX_INSTRUCTIONS_ACTION.UPDATE_CURRENT_STEP_STATUS,
            status: AX_INSTRUCTIONS_STATUS.COMPLETED,
        })
    }

    // mark step as complete on reaching the next btn if key is not required for assessment
    useEffect(() => {
        if (isNextBtnInView && !store?.assessment.is_key_required) {
            markStepAsComplete()
        }
    }, [isNextBtnInView, store?.assessment.is_key_required])

    useEffect(() => {
        if (store.otp) {
            markStepAsComplete()
        }
    }, [])

    return (
        <Flex
            pl={16}
            pr={16}
            pt={'167px'}
            flexDirection={'column'}
            minHeight="100vh"
        >
            <Content
                customInstructions={store.assessment.custom_instructions}
                proctoringType={store.assessment.proctoring}
            />

            <Flex flexDirection={'column'} mt={'auto'}>
                <Divider borderBottom={assessmentDivider} />
                <NavFooter
                    nextBtnRef={nextBtnRef}
                    dispatch={dispatch}
                    isNextDisabled={false}
                />
            </Flex>
        </Flex>
    )
}

export default InstructionsWithOtp
